import React, { useEffect, useState, Component } from 'react';
import { Activity, Database, Terminal, Code, Layers, Search, Cpu, Server, Wrench, Zap, RefreshCw, Trash2, TrendingUp, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, AppState, GroupId } from '../../types';
import { useAppStore } from '../../hooks/useAppStore';
import { isStrictMode, setStrictMode } from '../../utils/strictMode';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { TaxonomyCoverage } from './coverage/TaxonomyCoverage';
import { SeedStatus } from './diagnostics/SeedStatus';
import { QualityWorkspace } from './quality/QualityWorkspace';
import { ValidationRunPanel } from './validation/ValidationRunPanel';
// Import existing panels
import { DevSystemPanel } from './overview/DevSystemPanel';
import { QuestionTypesPanel } from './overview/QuestionTypesPanel';
import { QuestionExplorer } from './data/QuestionExplorer';
import { FamilyExplorer } from './data/FamilyExplorer';
import { StateExplorer } from './data/StateExplorer';
interface DeveloperPageProps {
  questions: Question[];
  state: AppState;
  selectedGroup: GroupId;
  onNavigate?: (view: string) => void;
}
type TabId = 'system' | 'data' | 'coverage' | 'quality' | 'validation' | 'tools';
const TABS: {
  id: TabId;
  label: string;
  icon: any;
}[] = [{
  id: 'system',
  label: 'Systeem',
  icon: Activity
}, {
  id: 'data',
  label: 'Data',
  icon: Database
}, {
  id: 'coverage',
  label: 'Coverage',
  icon: Layers
}, {
  id: 'quality',
  label: 'Kwaliteit',
  icon: Zap
}, {
  id: 'validation',
  label: 'Validatie',
  icon: Cpu
}, {
  id: 'tools',
  label: 'Tools',
  icon: Wrench
}];
export function DeveloperPage({
  questions,
  state,
  selectedGroup,
  onNavigate
}: DeveloperPageProps) {
  const {
    devJumpToLevel,
    devResetProgress
  } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabId>('system');
  const [refreshKey, setRefreshKey] = useState(0);
  const [strictModeEnabled, setStrictModeEnabled] = useState(isStrictMode());
  const toggleStrictMode = () => {
    const newState = !strictModeEnabled;
    setStrictMode(newState);
    setStrictModeEnabled(newState);
    setRefreshKey(k => k + 1);
  };
  const handleRefresh = () => setRefreshKey(k => k + 1);
  return <div className="space-y-6" data-id="element-1599">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1600">
        <div className="flex items-center gap-3" data-id="element-1601">
          <div className="p-2.5 bg-indigo-100 text-indigo-600 rounded-xl" data-id="element-1602">
            <Terminal className="w-6 h-6" data-id="element-1603" />
          </div>
          <div data-id="element-1604">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight" data-id="element-1605">
              Developer Console
            </h1>
            <p className="text-sm text-slate-500 font-medium" data-id="element-1606">
              System Internals & Data Observatory
            </p>
          </div>
        </div>
        <Badge variant="neutral" className="self-start sm:self-auto" data-id="element-1607">
          RC 4.1
        </Badge>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1.5 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto scrollbar-hide" data-id="element-1608">
        {TABS.map(tab => {
        const isActive = activeTab === tab.id;
        const Icon = tab.icon;
        return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap
                ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
              `} data-id="element-1609">
              <Icon className="w-4 h-4" data-id="element-1610" />
              {tab.label}
            </button>;
      })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait" data-id="element-1611">
        <motion.div key={activeTab} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} data-id="element-1612">
          {/* ── TAB: SYSTEM ── */}
          {activeTab === 'system' && <div className="space-y-6" data-id="element-1613">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1614">
                {/* Main System Panel */}
                <div className="lg:col-span-2 space-y-6" data-id="element-1615">
                  <DevSystemPanel state={state} questions={questions} selectedGroup={selectedGroup} refreshKey={refreshKey} onRefresh={handleRefresh} onJumpToLevel={devJumpToLevel} onResetProgress={devResetProgress} onOpenGovernance={() => onNavigate?.('governance')} data-id="element-1616" />

                  <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1617">
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-1618">
                      <div className="flex items-center gap-2" data-id="element-1619">
                        <Code className="w-5 h-5 text-slate-500" data-id="element-1620" />
                        <h3 className="font-bold text-slate-700" data-id="element-1621">
                          Question Types Registry
                        </h3>
                      </div>
                      <Badge variant="neutral" data-id="element-1622">Config</Badge>
                    </div>
                    <div className="p-6" data-id="element-1623">
                      <QuestionTypesPanel data-id="element-1624" />
                    </div>
                  </Card>
                </div>

                {/* Sidebar Stats */}
                <div className="space-y-6" data-id="element-1625">
                  <SystemResourcesCard questionsCount={questions.length} stateSize={JSON.stringify(state).length} data-id="element-1626" />

                  <Card className="p-5" data-id="element-1627">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4" data-id="element-1628">
                      Environment
                    </h3>
                    <div className="space-y-3" data-id="element-1629">
                      <div className="flex justify-between items-center text-sm" data-id="element-1630">
                        <span className="text-slate-600" data-id="element-1631">Build</span>
                        <span className="font-mono font-bold text-slate-800" data-id="element-1632">
                          20260225
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm" data-id="element-1633">
                        <span className="text-slate-600" data-id="element-1634">React</span>
                        <span className="font-mono font-bold text-slate-800" data-id="element-1635">
                          {React.version}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm" data-id="element-1636">
                        <span className="text-slate-600" data-id="element-1637">Mode</span>
                        <Badge variant={process.env.NODE_ENV === 'development' ? 'warning' : 'success'} size="sm" data-id="element-1638">
                          {process.env.NODE_ENV}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>}

          {/* ── TAB: DATA ── */}
          {activeTab === 'data' && <div className="space-y-6" data-id="element-1639">
              <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1640">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2" data-id="element-1641">
                  <Search className="w-5 h-5 text-slate-400" data-id="element-1642" />
                  <h3 className="font-bold text-slate-700" data-id="element-1643">
                    Question Explorer
                  </h3>
                </div>
                <div className="h-[600px]" data-id="element-1644">
                  <QuestionExplorer questions={questions} data-id="element-1645" />
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1646">
                <Card className="overflow-hidden border-slate-200 shadow-sm flex flex-col h-[500px]" data-id="element-1647">
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 flex-shrink-0" data-id="element-1648">
                    <Layers className="w-5 h-5 text-slate-400" data-id="element-1649" />
                    <h3 className="font-bold text-slate-700" data-id="element-1650">
                      Family Explorer
                    </h3>
                  </div>
                  <div className="flex-1 overflow-hidden" data-id="element-1651">
                    <FamilyExplorer questions={questions} data-id="element-1652" />
                  </div>
                </Card>

                <Card className="overflow-hidden border-slate-200 shadow-sm flex flex-col h-[500px]" data-id="element-1653">
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 flex-shrink-0" data-id="element-1654">
                    <Server className="w-5 h-5 text-slate-400" data-id="element-1655" />
                    <h3 className="font-bold text-slate-700" data-id="element-1656">State Explorer</h3>
                  </div>
                  <div className="flex-1 overflow-auto" data-id="element-1657">
                    <StateExplorer state={state} selectedGroup={selectedGroup} data-id="element-1658" />
                  </div>
                </Card>
              </div>
            </div>}

          {/* ── TAB: COVERAGE ── */}
          {activeTab === 'coverage' && <div className="space-y-6" data-id="element-1659">
              <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1660">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-1661">
                  <div className="flex items-center gap-2" data-id="element-1662">
                    <Layers className="w-5 h-5 text-slate-500" data-id="element-1663" />
                    <h3 className="font-bold text-slate-700" data-id="element-1664">
                      Taxonomy Coverage
                    </h3>
                  </div>
                  <Badge variant="neutral" data-id="element-1665">
                    {questions.length} vragen geladen
                  </Badge>
                </div>
                <div className="p-6" data-id="element-1666">
                  <TaxonomyCoverage questions={questions} data-id="element-1667" />
                </div>
              </Card>

              <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1668">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2" data-id="element-1669">
                  <Database className="w-5 h-5 text-slate-400" data-id="element-1670" />
                  <h3 className="font-bold text-slate-700" data-id="element-1671">Seed Status</h3>
                </div>
                <div className="p-6" data-id="element-1672">
                  <SeedStatus data-id="element-1673" />
                </div>
              </Card>
            </div>}

          {/* ── TAB: QUALITY ── */}
          {activeTab === 'quality' && <div data-id="element-1674">
              <QualityWorkspace refreshKey={refreshKey} data-id="element-1675" />
            </div>}

          {/* ── TAB: VALIDATION ── */}
          {activeTab === 'validation' && <div data-id="element-1676">
              <ValidationRunPanel questions={questions} data-id="element-1677" />
            </div>}

          {/* ── TAB: TOOLS ── */}
          {activeTab === 'tools' && <div className="max-w-3xl space-y-6" data-id="element-1678">
              <Card className="p-6" data-id="element-1679">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1680">
                  <Wrench className="w-5 h-5 text-indigo-600" data-id="element-1681" />
                  Developer Controls
                </h3>

                <div className="space-y-6" data-id="element-1682">
                  {/* Strict Mode Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1683">
                    <div data-id="element-1684">
                      <h4 className="font-bold text-slate-900" data-id="element-1685">Strict Mode</h4>
                      <p className="text-sm text-slate-500" data-id="element-1686">
                        Bypass error boundaries to expose crashes
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-1687">
                      <input type="checkbox" checked={strictModeEnabled} onChange={toggleStrictMode} className="sr-only peer" data-id="element-1688" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600" data-id="element-1689"></div>
                    </label>
                  </div>

                  {/* Level Controls */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1690">
                    <h4 className="font-bold text-slate-900 mb-3" data-id="element-1691">
                      Level Simulation
                    </h4>
                    <div className="flex gap-3" data-id="element-1692">
                      <Button onClick={() => devJumpToLevel(1)} variant="secondary" size="sm" data-id="element-1693">
                        <RefreshCw className="w-4 h-4 mr-2" data-id="element-1694" />
                        Level 1
                      </Button>
                      <Button onClick={() => devJumpToLevel(5)} variant="secondary" size="sm" data-id="element-1695">
                        <TrendingUp className="w-4 h-4 mr-2" data-id="element-1696" />
                        Level 5
                      </Button>
                      <Button onClick={() => devJumpToLevel(10)} variant="secondary" size="sm" data-id="element-1697">
                        <TrendingUp className="w-4 h-4 mr-2" data-id="element-1698" />
                        Level 10
                      </Button>
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-1699">
                    <h4 className="font-bold text-slate-900 mb-3" data-id="element-1700">
                      Quick Navigation
                    </h4>
                    <Button onClick={() => onNavigate?.('governance')} variant="outline" size="sm" data-id="element-1701">
                      Open Governance Console
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1702" />
                    </Button>
                  </div>

                  {/* Danger Zone */}
                  <div className="p-4 bg-rose-50 rounded-xl border border-rose-100" data-id="element-1703">
                    <h4 className="font-bold text-rose-900 mb-3" data-id="element-1704">
                      Danger Zone
                    </h4>
                    <Button onClick={devResetProgress} variant="danger" size="sm" className="w-full sm:w-auto" data-id="element-1705">
                      <Trash2 className="w-4 h-4 mr-2" data-id="element-1706" />
                      Reset All Progress
                    </Button>
                  </div>
                </div>
              </Card>
            </div>}
        </motion.div>
      </AnimatePresence>
    </div>;
}
// --- Helper Components ---
function SystemResourcesCard({
  questionsCount,
  stateSize
}: {
  questionsCount: number;
  stateSize: number;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <Card className="p-6 bg-slate-900 text-white border-0 relative overflow-hidden shadow-xl shadow-slate-900/10" data-id="element-1707">
      {/* Gradient Mesh Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-emerald-500/10 pointer-events-none" data-id="element-1708" />
      <div className="absolute -right-10 -top-10 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" data-id="element-1709" />

      <div className="relative z-10" data-id="element-1710">
        <div className="flex items-center gap-3 mb-6" data-id="element-1711">
          <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm" data-id="element-1712">
            <Cpu className="w-5 h-5 text-indigo-300" data-id="element-1713" />
          </div>
          <h3 className="text-lg font-bold" data-id="element-1714">System Resources</h3>
        </div>

        <div className="space-y-6" data-id="element-1715">
          <div data-id="element-1716">
            <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider" data-id="element-1717">
              <span data-id="element-1718">Questions Loaded</span>
              <span className="text-white" data-id="element-1719">{questionsCount}</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1720">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-1000 ease-out" style={{
              width: mounted ? '100%' : '0%'
            }} data-id="element-1721" />
            </div>
          </div>

          <div data-id="element-1722">
            <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider" data-id="element-1723">
              <span data-id="element-1724">State Size</span>
              <span className="text-white" data-id="element-1725">
                {(stateSize / 1024).toFixed(1)} KB
              </span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1726">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-1000 ease-out delay-150" style={{
              width: mounted ? '25%' : '0%'
            }} data-id="element-1727" />
            </div>
          </div>

          <div className="pt-4 mt-2 border-t border-white/10 flex items-center justify-between text-xs text-slate-400" data-id="element-1728">
            <div className="flex items-center gap-2" data-id="element-1729">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1730" />
              <span data-id="element-1731">System Operational</span>
            </div>
            <span className="font-mono opacity-50" data-id="element-1732">MEM_OK</span>
          </div>
        </div>
      </div>
    </Card>;
}